import React from 'react';
import EduTip from './EduTip.jsx';
import Select from './Select.jsx';
import Checkbox from './Checkbox.jsx';
import { INFLATION, FUEL } from '../data.js';

function Row({ label, children, hint, term }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-[10px] items-center py-1 border-b border-dashed border-[rgba(148,163,184,0.12)] last:border-b-0">
      <div className="flex items-center gap-[6px] text-[#cbd5e1] text-[12px]">
        <span>{label}</span>
        {term && <EduTip term={term} />}
      </div>
      <div className="text-[12px]">{children}</div>
      {hint && <div className="col-start-2 text-[#64748b] text-[10px] italic">{hint}</div>}
    </div>
  );
}

export default function InflationPanel({ scenario, setScenario }) {
  const set = (patch) => setScenario(s => ({ ...s, ...patch }));
  const title = 'm-0 text-[13px] text-[#f1f5f9] font-bold tracking-[0.02em] flex items-center gap-2';
  const titleGap = `${title} mt-3 pt-3 border-t border-dashed border-[#334155]`;
  return (
    <div className="flex flex-col gap-[10px]">
      <h4 className={title}>
        Inflation & Support Pressure <EduTip term="CPI" />
      </h4>

      <Row label="Headline CPI" term="CPI" hint="International demo headline assumption">
        <Select compact
          value={scenario.cpiAnnualPct ?? INFLATION.phCpiAnnualPct}
          onChange={(e) => set({ cpiAnnualPct: parseFloat(e.target.value) })}>
          {INFLATION.phCpiAnnualPct_options.map(v => <option key={v} value={v}>{`${(v * 100).toFixed(0)}%`}</option>)}
        </Select>
      </Row>

      <Row label="Input-cost CPI" hint="Input costs can outpace headline inflation">
        <Select compact
          value={scenario.foodCpiAnnualPct ?? INFLATION.foodCpiAnnualPct}
          onChange={(e) => set({ foodCpiAnnualPct: parseFloat(e.target.value) })}>
          {INFLATION.foodCpiAnnualPct_options.map(v => <option key={v} value={v}>{`${(v * 100).toFixed(0)}%`}</option>)}
        </Select>
      </Row>

      <Row label="Support pressure" hint="Optional pressure layer on top of CPI">
        <Select compact
          value={scenario.familyPressurePct ?? INFLATION.familyAllowancePressurePct}
          onChange={(e) => set({ familyPressurePct: parseFloat(e.target.value) })}>
          {INFLATION.familyAllowancePressurePct_options.map(v => <option key={v} value={v}>{`+${(v * 100).toFixed(0)}%`}</option>)}
        </Select>
      </Row>

      <h4 className={titleGap}>
        Energy cost <EduTip term="Fuel" />
      </h4>

      <Row label="Enable fuel line">
        <Checkbox
          checked={!!scenario.fuelEnabled}
          onChange={(v) => set({ fuelEnabled: v })}
        />
      </Row>

      <Row label="Base unit cost" hint="International demo baseline">
        <Select compact
          value={scenario.fuelPricePerLiter ?? FUEL.phPumpPricePerLiter}
          onChange={(e) => set({ fuelPricePerLiter: parseFloat(e.target.value) })}>
          {FUEL.phPumpPrice_options.map(v => <option key={v} value={v}>{`${v}/unit`}</option>)}
        </Select>
      </Row>

      <Row label="Monthly units consumed" hint="Operating usage baseline">
        <Select compact
          value={scenario.fuelMonthlyLiters ?? FUEL.monthlyLitersConsumed}
          onChange={(e) => set({ fuelMonthlyLiters: parseFloat(e.target.value) })}>
          {FUEL.monthlyLiters_options.map(v => <option key={v} value={v}>{`${v} L`}</option>)}
        </Select>
      </Row>

      <Row label="Geopolitical shock" hint="Iran/Israel/USA — oil wedge">
        <Select compact
          value={scenario.fuelShockPct ?? FUEL.geopoliticalShockPct}
          onChange={(e) => set({ fuelShockPct: parseFloat(e.target.value) })}>
          {FUEL.geopoliticalShock_options.map(v => <option key={v} value={v}>{`${v >= 0 ? '+' : ''}${(v * 100).toFixed(0)}%`}</option>)}
        </Select>
      </Row>
    </div>
  );
}
